import { useEffect, useState, useCallback, useRef, useId } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import {
  decorateAll, unreadCount, badgeLabel, relativeTime,
  type NotificationRow, type DecoratedNotification,
} from "@/lib/notifications";

export function NotificationBell({ light = false }: { light?: boolean }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("notifications")
      .select("id,title,message,channel,shipment_id,read_at,created_at")
      .order("created_at", { ascending: false })
      .limit(30);
    setRows((data ?? []) as NotificationRow[]);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  // Keep the latest loader without forcing the realtime channel to re-subscribe.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // Unique per mounted bell (desktop rail + mobile header can both be mounted).
  const instanceId = useId();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications:${user.id}:${instanceId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => { void loadRef.current(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, instanceId]);

  const unread = unreadCount(rows);
  const badge = badgeLabel(unread);
  const items: DecoratedNotification[] = decorateAll(rows).slice(0, 8);

  const markRead = async (ids: string[]) => {
    if (ids.length === 0) return;
    const stamp = new Date().toISOString();
    setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, read_at: r.read_at ?? stamp } : r)));
    await supabase.from("notifications").update({ read_at: stamp }).in("id", ids).is("read_at", null);
  };

  const openItem = async (n: DecoratedNotification) => {
    await markRead([n.id]);
    setOpen(false);
    if (n.to) nav(n.to);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
          className={cn("relative min-h-11 min-w-11", light && "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground")}
        >
          <Bell className="h-5 w-5" />
          {badge && (
            <span className="absolute right-1.5 top-1.5 min-w-4 rounded-full bg-primary px-1 text-[10px] font-bold leading-4 text-primary-foreground">
              {badge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(22rem,90vw)] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-8 text-xs"
              onClick={() => markRead(rows.filter((r) => !r.read_at).map((r) => r.id))}>
              <CheckCheck className="mr-1 h-3.5 w-3.5" />Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 divide-y divide-border overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>
          )}
          {items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)}
              className="flex w-full gap-2 px-3 py-3 text-left transition-colors hover:bg-accent">
              <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.unread ? "bg-primary" : "bg-transparent")} aria-hidden />
              <span className="min-w-0">
                <span className={cn("block truncate text-sm", n.unread ? "font-semibold" : "font-medium")}>{n.title}</span>
                {n.message && <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">{n.message}</span>}
                <span className="mt-1 block text-[11px] text-muted-foreground">{relativeTime(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="border-t border-border p-2">
          <Button asChild variant="ghost" size="sm" className="w-full" onClick={() => setOpen(false)}>
            <Link to="/app/notifications">View all notifications</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
